import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import SearchBar from '../../../components/SearchBar';
import NewFilterDrawer from '../../../components/shared/FilterDrawer/NewFilterDrawer';
import PostingTabToolbar from '../../../components/shared/PostingTabToolbar';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../../constants/FilterConstants';
import { DEFAULT_PAGE } from '../../../constants/pagination';
import MonthlyReconciliationTable from './MonthlyReconciliationTable';
import { MESSAGE } from '../../../constants/message';
import { buildMonthlyFinalizedFilterData } from './helpers/buildFilterOptions';

const MONTHLY_FINALIZED_FILTER_CONFIG = {
  searchFields: ['postingId', 'productType', 'approverName'],
  productKey: FILTER_VALUES.PRODUCT_TYPE,
  productAccessor: 'productType',
  createdByKey: FILTER_VALUES.CREATED_BY,
  createdByAccessor: 'approverId',
  startDateKey: FILTER_VALUES.START_DATE,
  endDateKey: FILTER_VALUES.END_DATE,
  startDateAccessor: 'startDate',
};

const MonthlyReconciliationTab = ({
  tabKey,
  data,
  filterDefinition,
  setFiltersAction,
  resetFiltersAction,
  columns,
  sortComparators,
  detailRoute,
  filterOptions,
  noDataTitleMessageKey,
  searchPlaceholder,
  initialSortBy = null,
  sortBy,
  onSortChange,
  totalCount,
  pageNumber,
  rowsPerPage,
  onPageChange,
  onPerPageChange,
}) => {
  const { DRAWER_TYPES, TITLE } = FILTER_CONSTANTS;
  const dispatch = useDispatch();

  const tabState = useSelector((state) => state.monthlyReconciliationFilter?.[tabKey]) || {};
  const { searchText = '', ...filterParams } = tabState;
  const drawerFilterKeys = useMemo(
    () => Object.keys(filterDefinition?.INITIAL_PARAMS ?? {}),
    [filterDefinition?.INITIAL_PARAMS],
  );
  const drawerFilterParams = useMemo(
    () =>
      drawerFilterKeys.reduce((acc, key) => {
        acc[key] = filterParams?.[key] ?? filterDefinition?.INITIAL_PARAMS?.[key];
        return acc;
      }, {}),
    [drawerFilterKeys, filterParams, filterDefinition?.INITIAL_PARAMS],
  );

  const filterData = useMemo(
    () => buildMonthlyFinalizedFilterData(filterDefinition?.FILTER, filterOptions, drawerFilterParams),
    [filterDefinition?.FILTER, filterOptions, drawerFilterParams],
  );

  const handleApplyFilter = useCallback(
    (filters) => {
      const start = filters?.[FILTER_VALUES.START_DATE] || '';
      const end = filters?.[FILTER_VALUES.END_DATE] || '';
      if (start && end && dayjs(end).isBefore(dayjs(start))) {
        dispatch(setFiltersAction({ ...tabState, ...filters, [FILTER_VALUES.END_DATE]: '', pageNumber: DEFAULT_PAGE }));
        return;
      }
      dispatch(setFiltersAction({ ...tabState, ...filters, pageNumber: DEFAULT_PAGE }));
    },
    [dispatch, setFiltersAction, tabState],
  );

  const handleSearch = (value) =>
    dispatch(setFiltersAction({ ...tabState, searchText: value, pageNumber: DEFAULT_PAGE }));
  const handleSearchReset = () => dispatch(setFiltersAction({ ...tabState, searchText: '', pageNumber: DEFAULT_PAGE }));

  const emptyFilterLength = useMemo(
    () => JSON.stringify(filterDefinition?.INITIAL_PARAMS ?? {}).length,
    [filterDefinition?.INITIAL_PARAMS],
  );

  return (
    <>
      <PostingTabToolbar
        searchBar={
          <SearchBar
            onSearch={handleSearch}
            placeholder={searchPlaceholder}
            onReset={handleSearchReset}
            searchText={searchText}
          />
        }
        filters={
          <NewFilterDrawer
            filteredValues={drawerFilterParams}
            name={TITLE.FILTERS.toUpperCase()}
            title={TITLE.FILTERS}
            drawerType={DRAWER_TYPES.FILTER_DRAWER}
            onReset={() => dispatch(resetFiltersAction())}
            onApplyFilter={handleApplyFilter}
            filterData={filterData}
            emptyFilterLength={emptyFilterLength}
          />
        }
      />

      <MonthlyReconciliationTable
        data={data}
        searchText={searchText}
        filterParams={drawerFilterParams}
        config={MONTHLY_FINALIZED_FILTER_CONFIG}
        columns={columns}
        sortComparators={sortComparators}
        detailRoute={detailRoute}
        noDataTitle={MESSAGE[noDataTitleMessageKey] || 'No data found'}
        noDataDescription={MESSAGE.NO_FILTERED_DATA_DESCRIPTION}
        initialSortBy={initialSortBy}
        sortBy={sortBy}
        onSortChange={onSortChange}
        totalCount={totalCount}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
      />
    </>
  );
};

export default MonthlyReconciliationTab;
